import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, Views } from 'react-big-calendar';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

import type { ILead } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import {
    formatAvailabilitySummary,
    getBuildingLocalDateTime,
    getDayAvailability,
    isCalendarSlotOutsideTouringHours,
    useProperties,
    useTouringHours,
} from 'src/services/api';
import { useLeads } from 'src/services/api/leads/use-leads';
import CreateOrUpdateTourModal from 'src/components/create-or-update-tour-modal/CreateOrUpdateTourModal';
import TourDetailPopover from 'src/pages/landlord/tours/components/calendar/TourDetailPopover';
import { useMobileCalendarDaySlot } from 'src/hooks/use-mobile-calendar-day-slot';
import {
    formatDateTimeForApi,
    getTimezoneAbbr,
    getUserTimezone,
    parseDateTimeInUserTimezone,
} from 'src/utils/format-date-time';
import { startSentryReplaySampled } from 'src/utils/start-sentry-replay';
import { analyticsClient, AnalyticsEvent } from 'src/services/analytics';
import {
    CANCELED_TOUR_COLOR,
} from 'src/pages/landlord/tours/components/calendar/property-colors';
import {
    buildPropertyCalendarMaps,
    getCalendarDayTimes,
    type ITourCalendarEvent,
    toCalendarWallClockDate,
    tourCalendarLocalizer,
} from 'src/pages/landlord/tours/components/calendar/tour-calendar-utils';
import {
    collectFamilyPropertyIds,
    resolveRootId,
} from 'src/components/lead-sidebar/property-family';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import styles from 'src/components/lead-sidebar/LeadSidebarDayCalendar.module.css';

interface Props {
    tours: IShowing[];
    lead: ILead;
    /**
     * Called when an empty slot is clicked, with the clicked time as an
     * API-ready ISO timestamp (already resolved in the user's configured
     * timezone) and the property currently selected in the filter (or the
     * lead's property), so the parent can open the create-tour modal prefilled.
     */
    onSchedule: (startIso: string, propertyId: number | null) => void;
    /**
     * When set (e.g. the property chosen in the create-tour modal), the calendar
     * follows it: the filter switches to this property so the greyed-out touring
     * hours match the property being booked. Null clears back to all properties.
     */
    focusedPropertyId?: number | null;
}

function TourEventContent({ event }: { event: ITourCalendarEvent }) {
    return (
        <div className={styles.eventContent} data-testid="lead-sidebar-tour-event">
            {event.title}
        </div>
    );
}

/**
 * Day-view calendar for the lead message sidebar: a header with day navigation,
 * a clickable date that opens a mini month-picker, and a property filter that
 * defaults to the lead's property. Clicking an empty slot schedules a tour;
 * clicking a tour opens its detail popover.
 */
export default function LeadSidebarDayCalendar({ tours, lead, onSchedule, focusedPropertyId }: Props) {
    const { userSettings } = useAuthenticatedState();
    const { data: properties } = useProperties();
    const { data: leads, isLoading: isLoadingLeads } = useLeads();
    const timezone = getUserTimezone(userSettings);

    const getNow = useCallback(
        () => new Date(parseDateTimeInUserTimezone(new Date().toISOString(), timezone).format('YYYY-MM-DDTHH:mm:ss')),
        [timezone]
    );

    const leadByUuid = useMemo(() => {
        const map = new Map<string, ILead>();
        (leads ?? []).forEach((l) => map.set(l.uuid, l));
        return map;
    }, [leads]);

    const { propertyNameById, propertyColorById } = useMemo(
        () => buildPropertyCalendarMaps(properties ?? []),
        [properties]
    );
    const [date, setDate] = useState<Date>(() => new Date());

    const [propertyFilter, setPropertyFilter] = useState<number | null>(
        lead.property_id ?? null
    );

    // Once properties load (or the lead changes), scope the filter up to the
    // lead property's building root so its child units are shown by default.
    useEffect(() => {
        setPropertyFilter(resolveRootId(properties ?? [], lead.property_id ?? null));
    }, [lead.property_id, properties]);

    // Follow the exact property chosen in the create-tour modal so the calendar
    // scopes to that unit and its greyed-out availability matches the property
    // being booked.
    useEffect(() => {
        if (focusedPropertyId !== undefined) {
            setPropertyFilter(focusedPropertyId);
        }
    }, [focusedPropertyId]);

    const scopedPropertyId = propertyFilter ?? lead.property_id ?? null;

    // The building family for the scoped property: the root plus all its
    // children. Both the dropdown and the calendar's tour list are scoped to
    // this set, so selecting a parent shows every leaf unit's tours. The memo
    // keeps the Set's identity stable for the visibleTours/events memos below.
    const familyPropertyIds = useMemo(
        () => collectFamilyPropertyIds(properties ?? [], resolveRootId(properties ?? [], scopedPropertyId)),
        [properties, scopedPropertyId]
    );

    const propertyFilterOptions = useMemo(() => {
        const all = properties ?? [];
        const rootId = resolveRootId(all, scopedPropertyId);
        const ids = new Set(familyPropertyIds);
        if (propertyFilter != null) {
            ids.add(propertyFilter);
        }
        return [...ids]
            .map((id) => all.find((property) => property?.id === id))
            .filter((property): property is NonNullable<typeof property> => property != null)
            // Building parent first, then its children alphabetically.
            .sort((a, b) => {
                if (a.id === rootId) {
                    return -1;
                }
                if (b.id === rootId) {
                    return 1;
                }
                return a.name.localeCompare(b.name);
            });
    }, [properties, familyPropertyIds, propertyFilter, scopedPropertyId]);
    const { data: touringHours, isLoading: isLoadingTouringHours } = useTouringHours(scopedPropertyId);
    // Availability is derived locally from the configured periods and special
    // days for the focused date, resolved in the building's timezone.
    const selectedDayAvailability = useMemo(() => {
        if (!touringHours?.is_set || !touringHours.timezone) {
            return undefined;
        }
        const buildingDate = getBuildingLocalDateTime(date, timezone, touringHours.timezone)
            .format('YYYY-MM-DD');
        return getDayAvailability(touringHours, buildingDate);
    }, [date, timezone, touringHours]);

    // The greyed-out slot shading depends on touring hours. Rendering the grid
    // before they resolve paints every slot white, then repaints the closed
    // slots grey — a visible flicker. Hold off rendering the calendar until the
    // shading inputs are ready. When no property is scoped there is no shading,
    // so nothing to wait for.
    const isAvailabilityLoading = scopedPropertyId !== null && isLoadingTouringHours;

    const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);

    const [popoverTour, setPopoverTour] = useState<IShowing | null>(null);
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
    const [editingTour, setEditingTour] = useState<IShowing | null>(null);

    // A child selection shows only that child's tours; selecting the building
    // parent (the family root) shows every unit's tours across the family.
    const rootPropertyId = resolveRootId(properties ?? [], scopedPropertyId);
    const visibleTours = useMemo(() => {
        if (propertyFilter === null) {
            return tours;
        }
        if (propertyFilter === rootPropertyId) {
            return tours.filter((tour) => familyPropertyIds.has(tour.property_id));
        }
        return tours.filter((tour) => tour.property_id === propertyFilter);
    }, [tours, propertyFilter, rootPropertyId, familyPropertyIds]);

    const events = useMemo<ITourCalendarEvent[]>(() => {
        // Hold off rendering any events until the leads list has loaded, so we
        // never flash the wrong (or "Archived lead") name on the grid.
        return isLoadingLeads ? [] : visibleTours.map((tour) => {
            const start = toCalendarWallClockDate(tour.start_time, timezone);
            const end = dayjs(start).add(30, 'minute').toDate();
            const propertyName = propertyNameById.get(tour.property_id) ?? 'Property';
            const tourLead = tour.lead_uuid === lead.uuid ?
                lead :
                leadByUuid.get(tour.lead_uuid);
            // Leads missing from the (archived-excluding) leads list still have
            // their name embedded on the tour by the backend join.
            const leadName = tourLead?.name || tour.lead?.name || 'Archived lead';
            return {
                title: tour.is_canceled ?
                    `${propertyName} — Canceled tour` :
                    `${leadName} — ${propertyName}`,
                start,
                end,
                showing: tour,
            };
        });
    }, [visibleTours, timezone, propertyNameById, lead, leadByUuid, isLoadingLeads]);

    const eventPropGetter = (event: ITourCalendarEvent) => {
        const color = event.showing.is_canceled ?
            CANCELED_TOUR_COLOR :
            propertyColorById.get(event.showing.property_id) ?? CANCELED_TOUR_COLOR;
        return {
            className: `${styles.event} ${event.showing.is_canceled ? styles.eventCanceled : ''}`,
            // Only the per-property left-bar color is dynamic; the rest is in CSS.
            style: { borderLeftColor: color },
        };
    };

    const slotPropGetter = useCallback((slotDate: Date) => {
        if (scopedPropertyId === null) {
            return {};
        }
        return isCalendarSlotOutsideTouringHours({
            slotStart: slotDate,
            userTimezone: timezone,
            touringHours,
        }) ? { className: styles.outsideTouringHoursSlot } : {};
    }, [scopedPropertyId, timezone, touringHours]);

    const handleSelectSlot = useCallback((slotInfo: { start: Date }) => {
        analyticsClient.track(AnalyticsEvent.LEAD_SIDEBAR_TOUR_CALENDAR_CLICKED, {
            leadUuid: lead.uuid,
            propertyId: propertyFilter ?? lead.property_id,
        });
        startSentryReplaySampled();
        const propertyId =
            propertyFilter ?? lead.property_id;
        // The grid is rendered in the user's timezone wall-clock, so interpret
        // the clicked wall-time in that timezone and emit an absolute ISO
        // timestamp. This avoids the browser-vs-user timezone shift that a plain
        // dayjs(date).format() would introduce.
        const wallTime = dayjs(slotInfo.start).format('YYYY-MM-DDTHH:mm:ss');
        onSchedule(formatDateTimeForApi(dayjs(wallTime), timezone), propertyId);
    }, [lead, propertyFilter, timezone, onSchedule]);
    const handleMobileSelectSlot = useCallback(
        (start: Date) => handleSelectSlot({ start }),
        [handleSelectSlot]
    );
    const handleSelectEvent = useCallback(
        (event: ITourCalendarEvent, e: React.SyntheticEvent<HTMLElement>) => {
            setPopoverAnchor(e.currentTarget);
            setPopoverTour(event.showing);
        },
        []
    );
    const closePopover = useCallback(() => {
        setPopoverAnchor(null);
        setPopoverTour(null);
    }, []);
    const handlePopoverEdit = useCallback((showing: IShowing) => {
        setPopoverAnchor(null);
        setPopoverTour(null);
        setEditingTour(showing);
    }, []);
    const editingLead = editingTour ?
        (editingTour.lead_uuid === lead.uuid ?
            lead :
            leadByUuid.get(editingTour.lead_uuid)) :
        undefined;
    const calendarTimes = useMemo(() => getCalendarDayTimes(date, 12), [date]);
    const { isMobile, dayColumnWrapper } = useMobileCalendarDaySlot({
        min: calendarTimes.min,
        max: calendarTimes.max,
        onSelectSlot: handleMobileSelectSlot,
        testId: 'mobile-lead-sidebar-tour-day-column',
    });
    const calendarComponents = useMemo(
        () => ({ event: TourEventContent, dayColumnWrapper }),
        [dayColumnWrapper]
    );

    return (
        <Box className={styles.card}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                className={styles.toolbar}
            >
                <Stack direction="row" alignItems="center" className={styles.dateControls}>
                    <IconButton
                        size="small"
                        aria-label="Previous day"
                        onClick={() => setDate(dayjs(date).subtract(1, 'day').toDate())}
                        data-testid="lead-sidebar-calendar-previous-day-button"
                    >
                        <ChevronLeft size={18} />
                    </IconButton>
                    <IconButton
                        size="small"
                        aria-label="Next day"
                        onClick={() => setDate(dayjs(date).add(1, 'day').toDate())}
                        data-testid="lead-sidebar-calendar-next-day-button"
                    >
                        <ChevronRight size={18} />
                    </IconButton>
                    <Button
                        onClick={(e) => setPickerAnchor(e.currentTarget)}
                        endIcon={<ChevronDown size={12} />}
                        className={styles.dateButton}
                        data-testid="lead-sidebar-calendar-date-button"
                    >
                        <span>{dayjs(date).format('ddd, MMM D')}</span>
                    </Button>
                </Stack>

                <Select
                    size="small"
                    value={propertyFilter === null ? '' : String(propertyFilter)}
                    onChange={(e) =>
                        setPropertyFilter(
                            e.target.value === '' ?
                                null :
                                Number(e.target.value)
                        )
                    }
                    className={styles.propertySelect}
                    data-testid="lead-sidebar-calendar-property-select"
                >
                    {propertyFilterOptions.map((option) => {
                        const isParent = option.id === rootPropertyId;
                        return (
                            <MenuItem key={option.id} value={String(option.id)}>
                                {isParent ? option.name : `— ${option.name}`}
                            </MenuItem>
                        );
                    })}
                </Select>
            </Stack>

            <Popover
                open={Boolean(pickerAnchor)}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                        value={dayjs(date)}
                        onChange={(value) => {
                            if (value) {
                                setDate(value.toDate());
                            }
                            setPickerAnchor(null);
                        }}
                    />
                </LocalizationProvider>
            </Popover>

            <Box className={styles.calendar}>
                {touringHours?.is_set && (
                    <div className={styles.hoursSummary} data-testid="lead-sidebar-tour-hours-summary">
                        {formatAvailabilitySummary(selectedDayAvailability)}
                        {touringHours.timezone && ` (${getTimezoneAbbr(touringHours.timezone)})`}
                    </div>
                )}
                {isAvailabilityLoading ? null : (
                    <Calendar<ITourCalendarEvent>
                        localizer={tourCalendarLocalizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        date={date}
                        getNow={getNow}
                        onNavigate={setDate}
                        view={Views.DAY}
                        views={[Views.DAY]}
                        toolbar={false}
                        selectable={!isMobile}
                        onSelectSlot={handleSelectSlot}
                        onSelectEvent={handleSelectEvent}
                        eventPropGetter={eventPropGetter}
                        slotPropGetter={slotPropGetter}
                        components={calendarComponents}
                        onView={() => undefined}
                        min={calendarTimes.min}
                        max={calendarTimes.max}
                        scrollToTime={calendarTimes.scrollToTime}
                        step={30}
                        timeslots={2}
                        formats={{
                            timeGutterFormat: (value) => dayjs(value).format('h A'),
                        }}
                    />
                )}
            </Box>

            <TourDetailPopover
                tour={popoverTour}
                anchorEl={popoverAnchor}
                onClose={closePopover}
                onEdit={handlePopoverEdit}
            />

            {!!editingTour && !!editingLead && (
                <CreateOrUpdateTourModal
                    open={!!editingTour}
                    onClose={() => setEditingTour(null)}
                    leads={[editingLead]}
                    selectedLeadUuid={editingTour.lead_uuid}
                    landlordUserId={editingTour.landlord_user_id}
                    initDateTime={editingTour.start_time}
                    initPropertyId={editingTour.property_id}
                    showingId={editingTour.id}
                    sourceMessageUuid={editingTour.source_message_uuid || undefined}
                />
            )}
        </Box>
    );
}
